import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const EMPTY_FORM = {
    tipo_cliente: 'persona_natural',
    tipo_documento: 'cedula',
    documento: '',
    primer_apellido: '',
    segundo_apellido: '',
    primer_nombre: '',
    segundo_nombre: '',
    razon_social: '',
    telefono: '',
    email: '',
    direccion: '',
    barrio: '',
    estrato: '',
    pais: 'Colombia',
    departamento: 'Valle del Cauca',
    ciudad: '',
    fecha_nacimiento_dia: '',
    fecha_nacimiento_mes: '',
    fecha_nacimiento_anio: '',
    como_conocio: '',
    referencia_nombre: '',
    referencia_telefono: ''
};

const ESTADO_COLOR = {
    creado: 'bg-blue-100 text-blue-700',
    facturando: 'bg-green-100 text-green-700',
    suspendido: 'bg-red-100 text-red-600',
    retirado: 'bg-gray-100 text-gray-500'
};

const Clientes = () => {
    const { can, user } = useAuth();
    const [clientes, setClientes] = useState([]);

    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('basico');
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingUuid, setEditingUuid] = useState(null);
    const [deletingUuid, setDeletingUuid] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/clientes');
            setClientes(data);
        } catch (err) {
            if (err.response?.status !== 403) {
                setError('Error cargando datos');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) loadData();
    }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingUuid(null);
        setActiveTab('basico');
        setError('');
        setModalOpen(true);
    };

    // Parsear fecha de nacimiento (YYYY-MM-DD) a día/mes/año separados
    const parsearFechaNacimiento = (fecha) => {
        if (!fecha) return { dia: '', mes: '', anio: '' };
        const partes = fecha.substring(0, 10).split('-');
        return { dia: partes[2] || '', mes: partes[1] || '', anio: partes[0] || '' };
    };

    const openEdit = (cliente) => {
        const fn = parsearFechaNacimiento(cliente.fecha_nacimiento);
        setForm({
            tipo_cliente: cliente.tipo_cliente || 'persona_natural',
            tipo_documento: cliente.tipo_documento || 'cedula',
            documento: cliente.documento || '',
            primer_apellido: cliente.primer_apellido || '',
            segundo_apellido: cliente.segundo_apellido || '',
            primer_nombre: cliente.primer_nombre || '',
            segundo_nombre: cliente.segundo_nombre || '',
            razon_social: cliente.razon_social || '',
            telefono: cliente.telefono || '',
            email: cliente.email || '',
            direccion: cliente.direccion || '',
            barrio: cliente.barrio || '',
            estrato: cliente.estrato || '',
            pais: cliente.pais || 'Colombia',
            departamento: cliente.departamento || 'Valle del Cauca',
            ciudad: cliente.ciudad || '',
            fecha_nacimiento_dia: fn.dia,
            fecha_nacimiento_mes: fn.mes,
            fecha_nacimiento_anio: fn.anio,
            como_conocio: cliente.como_conocio || '',
            referencia_nombre: cliente.referencia_nombre || '',
            referencia_telefono: cliente.referencia_telefono || '',
            estado: cliente.estado || 'creado'
        });
        setEditingUuid(cliente.uuid);
        setActiveTab('basico');
        setError('');
        setModalOpen(true);
    };

    const handleDelete = async (cliente) => {
        if (!window.confirm(`¿Marcar como retirado al cliente "${cliente.nombre_completo}"?`)) return;
        setDeletingUuid(cliente.uuid);
        try {
            await api.delete(`/clientes/${cliente.uuid}`);
            showSuccess('Cliente marcado como retirado');
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error eliminando cliente');
        } finally {
            setDeletingUuid(null);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => {
            const next = { ...prev, [name]: value };
            // Si cambia tipo_cliente, limpiamos campos de nombre/razon social
            if (name === 'tipo_cliente') {
                next.primer_apellido = '';
                next.segundo_apellido = '';
                next.primer_nombre = '';
                next.segundo_nombre = '';
                next.razon_social = '';
            }
            return next;
        });
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            // Construir fecha_nacimiento desde los 3 campos separados
            const { fecha_nacimiento_dia, fecha_nacimiento_mes, fecha_nacimiento_anio, ...resto } = form;
            let fechaNacimiento = null;
            if (fecha_nacimiento_dia && fecha_nacimiento_mes && fecha_nacimiento_anio) {
                fechaNacimiento = `${fecha_nacimiento_anio}-${fecha_nacimiento_mes}-${fecha_nacimiento_dia.padStart(2, '0')}`;
            }
            const payload = { ...resto, fecha_nacimiento: fechaNacimiento };

            if (editingUuid) {
                await api.put(`/clientes/${editingUuid}`, payload);
                showSuccess('Cliente actualizado correctamente');
            } else {
                await api.post('/clientes', payload);
                showSuccess('Cliente creado correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando cliente');
        } finally {
            setSaving(false);
        }
    };

    const filtered = clientes.filter(c =>
        `${c.nombre_completo} ${c.documento} ${c.telefono || ''} ${c.ciudad || ''}`
            .toLowerCase().includes(search.toLowerCase())
    );

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Clientes</h2>
                {can('clientes', 'create') && (
                    <button
                        onClick={openCreate}
                        className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                    >
                        + Agregar Nuevo
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            {/* Buscador */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Buscar por nombre, documento, teléfono o ciudad..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                />
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron clientes</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Cliente</th>
                                <th className="px-4 py-3 text-left">Documento</th>
                                <th className="px-4 py-3 text-left">Teléfono</th>
                                <th className="px-4 py-3 text-left">Ciudad</th>
                                <th className="px-4 py-3 text-left">Estado</th>
                                {(can('clientes', 'update') || can('clientes', 'delete')) && (
                                    <th className="px-4 py-3 text-right">Acciones</th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(cliente => (
                                <tr key={cliente.uuid} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                                style={{ backgroundColor: 'var(--color-primary)' }}
                                            >
                                                {cliente.nombre_completo?.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-800">{cliente.nombre_completo}</p>
                                                <p className="text-xs text-gray-400">{cliente.email || '—'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                        {cliente.tipo_documento === 'nit' ? 'NIT' : 'CC'} {cliente.documento}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">{cliente.telefono || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500">{cliente.ciudad || '—'}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLOR[cliente.estado] || 'bg-gray-100 text-gray-500'}`}>
                                            {cliente.estado}
                                        </span>
                                    </td>
                                    {(can('clientes', 'update') || can('clientes', 'delete')) && (
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                {can('clientes', 'update') && (
                                                    <button
                                                        onClick={() => openEdit(cliente)}
                                                        className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                                                    >
                                                        Editar
                                                    </button>
                                                )}
                                                {can('clientes', 'delete') && cliente.estado !== 'retirado' && (
                                                    <button
                                                        onClick={() => handleDelete(cliente)}
                                                        disabled={deletingUuid === cliente.uuid}
                                                        className="text-xs font-medium px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                                    >
                                                        {deletingUuid === cliente.uuid ? '...' : 'Retirar'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingUuid ? 'Editar cliente' : 'Crear nuevo cliente'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-100 px-6">
                            <button
                                onClick={() => setActiveTab('basico')}
                                className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
                                style={activeTab === 'basico'
                                    ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }
                                    : { borderColor: 'transparent', color: '#9CA3AF' }}
                            >
                                Datos del Cliente
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            {/* ===================== TAB BÁSICO ===================== */}
                            {activeTab === 'basico' && (
                                <>
                                    {/* Tipo de cliente */}
                                    <div>
                                        <label className={labelClass}>Tipo de cliente</label>
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-2 text-sm">
                                                <input
                                                    type="radio"
                                                    name="tipo_cliente"
                                                    value="persona_natural"
                                                    checked={form.tipo_cliente === 'persona_natural'}
                                                    onChange={handleChange}
                                                />
                                                Persona Natural
                                            </label>
                                            <label className="flex items-center gap-2 text-sm">
                                                <input
                                                    type="radio"
                                                    name="tipo_cliente"
                                                    value="empresa"
                                                    checked={form.tipo_cliente === 'empresa'}
                                                    onChange={handleChange}
                                                />
                                                Empresa
                                            </label>
                                        </div>
                                    </div>

                                    {/* Tipo y número de documento */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Tipo de documento *</label>
                                            <select name="tipo_documento" value={form.tipo_documento} onChange={handleChange} className={inputClass}>
                                                <option value="cedula">Cédula</option>
                                                <option value="nit">NIT</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Documento *</label>
                                            <input name="documento" value={form.documento} onChange={handleChange} className={inputClass} placeholder="Solo números" />
                                        </div>
                                    </div>

                                    {/* Nombres / Razón social */}
                                    {form.tipo_cliente === 'empresa' ? (
                                        <div>
                                            <label className={labelClass}>Razón social *</label>
                                            <input name="razon_social" value={form.razon_social} onChange={handleChange} className={inputClass} />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className={labelClass}>Primer apellido *</label>
                                                    <input name="primer_apellido" value={form.primer_apellido} onChange={handleChange} className={inputClass} />
                                                </div>
                                                <div>
                                                    <label className={labelClass}>Segundo apellido</label>
                                                    <input name="segundo_apellido" value={form.segundo_apellido} onChange={handleChange} className={inputClass} />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className={labelClass}>Primer nombre *</label>
                                                    <input name="primer_nombre" value={form.primer_nombre} onChange={handleChange} className={inputClass} />
                                                </div>
                                                <div>
                                                    <label className={labelClass}>Segundo nombre</label>
                                                    <input name="segundo_nombre" value={form.segundo_nombre} onChange={handleChange} className={inputClass} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Contacto */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Teléfono</label>
                                            <input name="telefono" value={form.telefono} onChange={handleChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Correo electrónico</label>
                                            <input name="email" type="email" value={form.email} onChange={handleChange} className={inputClass} />
                                        </div>
                                    </div>

                                    {/* Dirección y barrio */}
                                    <div>
                                        <label className={labelClass}>Dirección</label>
                                        <input name="direccion" value={form.direccion} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Barrio</label>
                                            <input name="barrio" value={form.barrio} onChange={handleChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Estrato</label>
                                            <select name="estrato" value={form.estrato} onChange={handleChange} className={inputClass}>
                                                <option value="">Seleccionar...</option>
                                                {[1, 2, 3, 4, 5, 6].map(e => <option key={e} value={e}>{e}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Ubicación */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className={labelClass}>País</label>
                                            <input name="pais" value={form.pais} onChange={handleChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Departamento</label>
                                            <input name="departamento" value={form.departamento} onChange={handleChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Ciudad</label>
                                            <input name="ciudad" value={form.ciudad} onChange={handleChange} className={inputClass} />
                                        </div>
                                    </div>

                                    {/* Fecha de nacimiento */}
                                    <div>
                                        <label className={labelClass}>Fecha de nacimiento</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            <select name="fecha_nacimiento_dia" value={form.fecha_nacimiento_dia} onChange={handleChange} className={inputClass}>
                                                <option value="">Día</option>
                                                {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                                                    <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                                                ))}
                                            </select>
                                            <select name="fecha_nacimiento_mes" value={form.fecha_nacimiento_mes} onChange={handleChange} className={inputClass}>
                                                <option value="">Mes</option>
                                                {[
                                                    ['01','Enero'],['02','Febrero'],['03','Marzo'],['04','Abril'],
                                                    ['05','Mayo'],['06','Junio'],['07','Julio'],['08','Agosto'],
                                                    ['09','Septiembre'],['10','Octubre'],['11','Noviembre'],['12','Diciembre']
                                                ].map(([val, label]) => (
                                                    <option key={val} value={val}>{label}</option>
                                                ))}
                                            </select>
                                            <select name="fecha_nacimiento_anio" value={form.fecha_nacimiento_anio} onChange={handleChange} className={inputClass}>
                                                <option value="">Año</option>
                                                {Array.from({length: 85}, (_, i) => 2010 - i).map(a => (
                                                    <option key={a} value={String(a)}>{a}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Referencia personal */}
                                    <div>
                                        <label className={labelClass}>Referencia personal — Nombre completo</label>
                                        <input name="referencia_nombre" value={form.referencia_nombre} onChange={handleChange} className={inputClass} placeholder="Nombre del familiar o contacto" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Referencia personal — Teléfono</label>
                                        <input name="referencia_telefono" value={form.referencia_telefono} onChange={handleChange} className={inputClass} placeholder="Número de contacto" />
                                    </div>

                                    {/* Cómo nos conoció */}
                                    <div>
                                        <label className={labelClass}>¿Cómo nos conoció?</label>
                                        <select name="como_conocio" value={form.como_conocio} onChange={handleChange} className={inputClass}>
                                            <option value="">Seleccionar...</option>
                                            <option value="voz_a_voz">Voz a voz</option>
                                            <option value="redes_sociales">Redes sociales</option>
                                            <option value="vendedor">Vendedor / Asesor comercial</option>
                                            <option value="publicidad">Publicidad</option>
                                            <option value="internet">Internet</option>
                                        </select>
                                    </div>

                                    {editingUuid && (
                                        <div>
                                            <label className={labelClass}>Estado</label>
                                            <select name="estado" value={form.estado || 'creado'} onChange={handleChange} className={inputClass}>
                                                <option value="creado">Creado</option>
                                                <option value="facturando">Facturando</option>
                                                <option value="suspendido">Suspendido</option>
                                                <option value="retirado">Retirado</option>
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : (editingUuid ? 'Guardar cambios' : 'Crear cliente')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clientes;
